<template>
  <div class="app-container seal-stock-page">
    <el-tabs v-model="activeTab" @tab-click="handleTabClick">
      <el-tab-pane label="库存一览" name="stock">
        <div class="head-container stock-filter-bar">
          <el-select
            v-model="stockQuery.ruleId"
            clearable
            filterable
            size="small"
            placeholder="触点规则"
            class="stock-filter-item"
            @change="onStockFilterChange"
          >
            <el-option
              v-for="c in ruleOptions"
              :key="c.id"
              :label="c.ruleName + ' (' + c.ruleCode + ')'"
              :value="c.id"
            />
          </el-select>
          <el-select
            v-model="stockQuery.baseId"
            clearable
            filterable
            size="small"
            placeholder="印章底部"
            class="stock-filter-item"
            @change="onStockFilterChange"
          >
            <el-option
              v-for="b in baseOptions"
              :key="b.id"
              :label="b.baseName + ' (' + b.baseCode + ')'"
              :value="b.id"
            />
          </el-select>
          <el-select
            v-model="stockQuery.handleId"
            clearable
            filterable
            size="small"
            placeholder="印章手柄"
            class="stock-filter-item"
            @change="onStockFilterChange"
          >
            <el-option
              v-for="h in handleOptions"
              :key="h.id"
              :label="h.handleName"
              :value="h.id"
            />
          </el-select>
          <el-input
            v-model="stockQuery.keyword"
            clearable
            size="small"
            placeholder="物料/规则/手柄"
            class="stock-filter-keyword"
            @keyup.enter.native="searchStockList"
          />
          <el-button type="primary" icon="el-icon-search" size="small" @click="searchStockList">查询</el-button>
          <el-button type="success" icon="el-icon-download" size="small" @click="openInDialog">入库</el-button>
          <el-button type="warning" icon="el-icon-upload2" size="small" @click="openOutDialog">打包出库</el-button>
          <el-button size="small" icon="el-icon-link" @click="copyStockBoardLink">复制库存看板链接</el-button>
        </div>
        <el-table
          v-loading="stockLoading"
          :data="stockList"
          size="small"
          border
          stripe
          highlight-current-row
          style="width:100%;margin-top:12px;"
        >
          <el-table-column label="物料编码" min-width="168" show-overflow-tooltip>
            <template slot-scope="scope">
              <el-button type="text" class="sku-link" @click="openSkuMovements(scope.row)">
                {{ scope.row.skuCode || '—' }}
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="底座图" width="72" align="center">
            <template slot-scope="scope">
              <el-image
                v-if="scope.row.appearanceImage"
                :src="toFullUrl(scope.row.appearanceImage)"
                :preview-src-list="[toFullUrl(scope.row.appearanceImage)]"
                fit="contain"
                class="thumb-img"
              />
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="底座信息" min-width="150" show-overflow-tooltip>
            <template slot-scope="scope">
              <template v-if="scope.row.baseId && scope.row.baseName">
                <div>{{ scope.row.baseName }}</div>
                <div class="sub-line">
                  <span v-if="scope.row.color">{{ scope.row.color }}</span>
                  <span v-if="scope.row.bottomSizeText"> · {{ scope.row.bottomSizeText }}</span>
                </div>
              </template>
              <el-tag v-else type="warning" size="mini">未绑定底座</el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="ruleName" label="触点规则" min-width="110" show-overflow-tooltip />
          <el-table-column label="手柄图" width="72" align="center">
            <template slot-scope="scope">
              <el-image
                v-if="scope.row.handleImage"
                :src="toFullUrl(scope.row.handleImage)"
                :preview-src-list="[toFullUrl(scope.row.handleImage)]"
                fit="contain"
                class="thumb-img"
              />
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="手柄信息" min-width="120" show-overflow-tooltip>
            <template slot-scope="scope">
              <div>{{ scope.row.handleName || '—' }}</div>
              <div v-if="scope.row.handleColor" class="sub-line">{{ scope.row.handleColor }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="stockQty" label="当前库存" width="96" align="center">
            <template slot-scope="scope">
              <span :class="['qty-num', (scope.row.stockQty || 0) <= 0 ? 'is-empty' : '']">
                {{ scope.row.stockQty != null ? scope.row.stockQty : 0 }}
              </span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="80" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="openSkuForm(scope.row)">编辑</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          style="margin-top: 16px; text-align: right;"
          background
          layout="total, prev, pager, next, sizes"
          :current-page="stockQuery.page + 1"
          :page-size="stockQuery.size"
          :page-sizes="[10, 20, 50]"
          :total="stockTotal"
          @current-change="(p) => { stockQuery.page = Math.max(p - 1, 0); loadStockList() }"
          @size-change="(s) => { stockQuery.size = s; stockQuery.page = 0; loadStockList() }"
        />
      </el-tab-pane>

      <el-tab-pane label="入库记录" name="in">
        <div class="head-container">
          <el-input
            v-model="inQuery.batchNo"
            clearable
            size="small"
            placeholder="入库批次号"
            style="width: 180px; margin-right: 8px;"
            @keyup.enter.native="loadInList"
          />
          <el-button type="primary" icon="el-icon-search" size="small" @click="loadInList">查询</el-button>
          <el-button type="success" size="small" @click="openInDialog">新建入库</el-button>
        </div>
        <el-table
          v-loading="inLoading"
          :data="inList"
          size="small"
          border
          stripe
          class="in-record-table"
          style="width:100%;margin-top:12px;"
        >
          <el-table-column type="expand">
            <template slot-scope="scope">
              <div class="in-record-expand">
                <el-table :data="scope.row.items || []" size="mini" border class="in-record-expand-table">
                  <el-table-column prop="skuCode" label="物料编码" min-width="150" show-overflow-tooltip />
                <el-table-column prop="ruleName" label="触点规则" min-width="110" show-overflow-tooltip />
                <el-table-column prop="baseName" label="底座" min-width="120" show-overflow-tooltip />
                <el-table-column prop="handleName" label="手柄" width="110" show-overflow-tooltip />
                <el-table-column prop="qty" label="数量" width="80" align="center" />
                <el-table-column label="进货价" width="100" align="right">
                  <template slot-scope="s">
                    <span v-if="s.row.purchasePrice != null">{{ Number(s.row.purchasePrice).toFixed(4) }}</span>
                    <span v-else>—</span>
                  </template>
                </el-table-column>
              </el-table>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="batchNo" label="入库批次" min-width="160" show-overflow-tooltip />
          <el-table-column prop="supplierName" label="供应商/来源" min-width="110" show-overflow-tooltip />
          <el-table-column prop="remark" label="备注" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.remark || '—' }}</template>
          </el-table-column>
          <el-table-column prop="totalQty" label="合计数量" width="90" align="center" />
          <el-table-column prop="operatorName" label="操作人" width="90" show-overflow-tooltip />
          <el-table-column prop="createTime" label="入库时间" width="160" />
          <el-table-column label="操作" width="72" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="openInDetail(scope.row)">明细</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          style="margin-top: 16px; text-align: right;"
          background
          layout="total, prev, pager, next, sizes"
          :current-page="inQuery.page + 1"
          :page-size="inQuery.size"
          :total="inTotal"
          @current-change="(p) => { inQuery.page = Math.max(p - 1, 0); loadInList() }"
          @size-change="(s) => { inQuery.size = s; inQuery.page = 0; loadInList() }"
        />
      </el-tab-pane>

      <el-tab-pane label="出库记录" name="out">
        <div class="head-container">
          <el-input
            v-model="outQuery.batchNo"
            clearable
            size="small"
            placeholder="出库批次号"
            style="width: 160px; margin-right: 8px;"
            @keyup.enter.native="loadOutList"
          />
          <el-input
            v-model="outQuery.orderNo"
            clearable
            size="small"
            placeholder="订单号"
            style="width: 140px; margin-right: 8px;"
            @keyup.enter.native="loadOutList"
          />
          <el-select
            v-model="outQuery.channel"
            clearable
            size="small"
            placeholder="渠道"
            style="width: 120px; margin-right: 8px;"
          >
            <el-option v-for="c in channels" :key="c.code" :label="c.label" :value="c.code" />
          </el-select>
          <el-button type="primary" icon="el-icon-search" size="small" @click="loadOutList">查询</el-button>
          <el-button type="warning" size="small" @click="openOutDialog">新建出库</el-button>
        </div>
        <el-table
          v-loading="outLoading"
          :data="outList"
          size="small"
          border
          stripe
          style="width:100%;margin-top:12px;"
        >
          <el-table-column type="expand">
            <template slot-scope="scope">
              <el-table :data="scope.row.items || []" size="mini" border>
                <el-table-column prop="skuCode" label="物料编码" min-width="150" show-overflow-tooltip />
                <el-table-column prop="baseName" label="底座" min-width="120" show-overflow-tooltip />
                <el-table-column prop="handleName" label="手柄" width="110" show-overflow-tooltip />
                <el-table-column prop="qty" label="数量" width="80" align="center" />
              </el-table>
            </template>
          </el-table-column>
          <el-table-column prop="batchNo" label="出库批次" min-width="160" show-overflow-tooltip />
          <el-table-column prop="orderNo" label="订单号" min-width="110" show-overflow-tooltip />
          <el-table-column label="渠道" width="88" align="center">
            <template slot-scope="scope">{{ channelLabel(scope.row.channel) }}</template>
          </el-table-column>
          <el-table-column prop="baseNames" label="底座信息" min-width="140" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.baseNames || '—' }}</template>
          </el-table-column>
          <el-table-column prop="handleNames" label="手柄信息" min-width="120" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.handleNames || '—' }}</template>
          </el-table-column>
          <el-table-column prop="receiverName" label="客户/收件人" min-width="100" show-overflow-tooltip />
          <el-table-column prop="totalQty" label="合计数量" width="90" align="center" />
          <el-table-column prop="createTime" label="出库时间" width="160" />
          <el-table-column label="操作" width="72" align="center" fixed="right">
            <template slot-scope="scope">
              <el-button type="text" size="mini" @click="openOutDetail(scope.row)">明细</el-button>
            </template>
          </el-table-column>
        </el-table>
        <el-pagination
          style="margin-top: 16px; text-align: right;"
          background
          layout="total, prev, pager, next, sizes"
          :current-page="outQuery.page + 1"
          :page-size="outQuery.size"
          :total="outTotal"
          @current-change="(p) => { outQuery.page = Math.max(p - 1, 0); loadOutList() }"
          @size-change="(s) => { outQuery.size = s; outQuery.page = 0; loadOutList() }"
        />
      </el-tab-pane>
    </el-tabs>

    <el-dialog title="编辑物料" :visible.sync="skuDialogVisible" width="520px" :close-on-click-modal="false">
      <el-form label-width="110px" size="small">
        <el-form-item label="物料编码">
          <el-input :value="skuForm.skuCode" disabled />
        </el-form-item>
        <el-form-item label="当前库存">
          <el-input :value="skuForm.stockQty != null ? skuForm.stockQty : 0" disabled />
        </el-form-item>
        <el-form-item label="触点规则" required>
          <el-select v-model="skuForm.ruleId" filterable placeholder="选择触点规则" style="width:100%;">
            <el-option v-for="c in ruleOptions" :key="c.id" :label="c.ruleName + ' (' + c.ruleCode + ')'" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="印章底部" required>
          <el-select v-model="skuForm.baseId" filterable placeholder="先维护底座再选择" style="width:100%;">
            <el-option v-for="b in baseOptions" :key="b.id" :label="b.baseName + ' (' + b.baseCode + ')'" :value="b.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="手柄" required>
          <el-select v-model="skuForm.handleId" filterable placeholder="选择手柄" style="width:100%;">
            <el-option v-for="h in handleOptions" :key="h.id" :label="h.handleName" :value="h.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="物料名称">
          <el-input v-model="skuForm.skuName" maxlength="255" placeholder="留空则按三项自动生成" />
        </el-form-item>
        <el-form-item label="状态">
          <el-radio-group v-model="skuForm.status">
            <el-radio :label="1">启用</el-radio>
            <el-radio :label="0">停用</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="skuForm.remark" type="textarea" :rows="2" maxlength="512" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="skuDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="skuSaving" @click="submitSkuForm">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="印章入库" :visible.sync="inDialogVisible" width="1100px" :close-on-click-modal="false">
      <el-form label-width="100px" size="small">
        <el-form-item label="供应商" required>
          <el-select v-model="inForm.supplierId" filterable placeholder="选择供应商" style="width: 100%;" @change="onInSupplierChange">
            <el-option v-for="s in supplierOptions" :key="s.id" :label="s.supplierName + ' (' + s.supplierCode + ')'" :value="s.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="inForm.remark" type="textarea" :rows="2" maxlength="512" />
        </el-form-item>
        <el-form-item label="入库明细" required>
          <el-table :data="inForm.items" size="mini" border style="width:100%;">
            <el-table-column label="触点规则" min-width="180">
              <template slot-scope="scope">
                <el-select
                  v-model="scope.row.ruleId"
                  filterable
                  placeholder="触点规则"
                  size="mini"
                  style="width:100%;"
                  @change="() => fillInPurchasePrice(scope.row)"
                >
                  <el-option v-for="c in ruleOptions" :key="c.id" :label="c.ruleName + ' (' + c.ruleCode + ')'" :value="c.id" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="印章底部" min-width="160">
              <template slot-scope="scope">
                <el-select
                  v-model="scope.row.baseId"
                  filterable
                  placeholder="印章底部"
                  size="mini"
                  style="width:100%;"
                  @change="() => fillInPurchasePrice(scope.row)"
                >
                  <el-option v-for="b in baseOptions" :key="b.id" :label="b.baseName + ' (' + b.baseCode + ')'" :value="b.id" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="手柄" width="130">
              <template slot-scope="scope">
                <el-select
                  v-model="scope.row.handleId"
                  filterable
                  placeholder="手柄"
                  size="mini"
                  style="width:100%;"
                  @change="() => fillInPurchasePrice(scope.row)"
                >
                  <el-option v-for="h in handleOptions" :key="h.id" :label="h.handleName" :value="h.id" />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="数量" width="120" align="center">
              <template slot-scope="scope">
                <el-input-number v-model="scope.row.qty" :min="1" :max="999999" size="mini" controls-position="right" style="width:100%;" />
              </template>
            </el-table-column>
            <el-table-column label="进货价" width="130" align="center">
              <template slot-scope="scope">
                <el-input-number
                  v-model="scope.row.purchasePrice"
                  :min="0"
                  :precision="4"
                  :step="0.1"
                  size="mini"
                  controls-position="right"
                  style="width:100%;"
                />
              </template>
            </el-table-column>
            <el-table-column label="" width="64" align="center">
              <template slot-scope="scope">
                <el-button type="text" style="color:#f56c6c;" @click="removeInLine(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button type="text" icon="el-icon-plus" style="margin-top:8px;" @click="addInLine">添加一行</el-button>
          <div class="form-tip">须选：触点规则 + 印章底部 + 手柄 + 数量；进货价默认取供应商维护价</div>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="inDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="inSubmitting" @click="submitIn">确认入库</el-button>
      </span>
    </el-dialog>

    <el-dialog title="打包出库" :visible.sync="outDialogVisible" width="860px" :close-on-click-modal="false">
      <el-alert
        title="按「触点规则 × 印章底部 × 手柄」物料出库，可多行打包。"
        type="info"
        :closable="false"
        show-icon
        style="margin-bottom: 12px;"
      />
      <el-form label-width="100px" size="small">
        <el-form-item label="订单号">
          <el-input v-model="outForm.orderNo" maxlength="128" />
        </el-form-item>
        <el-form-item label="出库渠道" required>
          <el-select v-model="outForm.channel" placeholder="请选择" style="width: 100%;">
            <el-option v-for="c in channels" :key="c.code" :label="c.label" :value="c.code" />
          </el-select>
        </el-form-item>
        <el-form-item label="客户/收件人">
          <el-input v-model="outForm.receiverName" maxlength="128" />
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="outForm.remark" type="textarea" :rows="2" maxlength="512" />
        </el-form-item>
        <el-form-item label="出库明细" required>
          <el-table :data="outForm.items" size="mini" border style="width:100%;">
            <el-table-column label="物料" min-width="320">
              <template slot-scope="scope">
                <el-select v-model="scope.row.skuId" filterable placeholder="选择物料" size="mini" style="width:100%;">
                  <el-option
                    v-for="s in skuOptions"
                    :key="s.id"
                    :label="skuOptionLabel(s)"
                    :value="s.id"
                    :disabled="(s.stockQty || 0) <= 0"
                  />
                </el-select>
              </template>
            </el-table-column>
            <el-table-column label="数量" width="130" align="center">
              <template slot-scope="scope">
                <el-input-number v-model="scope.row.qty" :min="1" :max="999999" size="mini" controls-position="right" style="width:100%;" />
              </template>
            </el-table-column>
            <el-table-column label="" width="64" align="center">
              <template slot-scope="scope">
                <el-button type="text" style="color:#f56c6c;" @click="removeOutLine(scope.$index)">删除</el-button>
              </template>
            </el-table-column>
          </el-table>
          <el-button type="text" icon="el-icon-plus" style="margin-top:8px;" @click="addOutLine">添加一行</el-button>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="outDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="outSubmitting" @click="submitOut">确认出库</el-button>
      </span>
    </el-dialog>

    <el-dialog :title="detailTitle" :visible.sync="detailVisible" width="860px">
      <el-descriptions v-if="detailHeader" :column="2" size="small" border>
        <el-descriptions-item label="批次号">{{ detailHeader.batchNo }}</el-descriptions-item>
        <el-descriptions-item label="合计数量">{{ detailHeader.totalQty }}</el-descriptions-item>
        <el-descriptions-item v-if="detailHeader.orderNo != null" label="订单号">{{ detailHeader.orderNo || '—' }}</el-descriptions-item>
        <el-descriptions-item v-if="detailHeader.channel" label="渠道">{{ channelLabel(detailHeader.channel) }}</el-descriptions-item>
        <el-descriptions-item v-if="detailHeader.supplierName != null" label="供应商">{{ detailHeader.supplierName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="操作人">{{ detailHeader.operatorName || '—' }}</el-descriptions-item>
        <el-descriptions-item label="时间">{{ detailHeader.createTime || '—' }}</el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ detailHeader.remark || '—' }}</el-descriptions-item>
      </el-descriptions>
      <el-table :data="detailItems" size="small" border stripe style="margin-top:12px;">
        <el-table-column prop="skuCode" label="物料编码" min-width="150" show-overflow-tooltip />
        <el-table-column prop="ruleName" label="触点规则" min-width="110" show-overflow-tooltip />
        <el-table-column prop="baseName" label="底座" min-width="120" show-overflow-tooltip />
        <el-table-column prop="handleName" label="手柄" width="110" show-overflow-tooltip />
        <el-table-column prop="qty" label="数量" width="90" align="center" />
        <el-table-column label="进货价" width="110" align="right">
          <template slot-scope="scope">
            <span v-if="scope.row.purchasePrice != null">{{ Number(scope.row.purchasePrice).toFixed(4) }}</span>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer">
        <el-button size="small" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <el-dialog
      :title="movementsTitle"
      :visible.sync="movementsVisible"
      width="960px"
      top="6vh"
      @closed="resetMovements"
    >
      <div v-loading="movementsLoading">
        <el-descriptions v-if="movementsSku" :column="3" size="small" border style="margin-bottom:14px;">
          <el-descriptions-item label="物料编码">{{ movementsSku.skuCode || '—' }}</el-descriptions-item>
          <el-descriptions-item label="底座">{{ movementsSku.baseName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="手柄">{{ movementsSku.handleName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="当前库存">{{ movementsSku.stockQty != null ? movementsSku.stockQty : 0 }}</el-descriptions-item>
          <el-descriptions-item label="触点规则">{{ movementsSku.ruleName || '—' }}</el-descriptions-item>
          <el-descriptions-item label="名称">{{ movementsSku.skuName || '—' }}</el-descriptions-item>
        </el-descriptions>
        <div class="movements-block">
          <div class="movements-title">入库记录（{{ movementsInList.length }}）</div>
          <el-table :data="movementsInList" size="small" border stripe max-height="280">
            <el-table-column prop="batchNo" label="入库批次" min-width="150" show-overflow-tooltip />
            <el-table-column prop="supplierName" label="供应商" min-width="110" show-overflow-tooltip />
            <el-table-column prop="ruleName" label="触点规则" min-width="110" show-overflow-tooltip />
            <el-table-column prop="baseName" label="底座" min-width="110" show-overflow-tooltip />
            <el-table-column prop="handleName" label="手柄" width="100" show-overflow-tooltip />
            <el-table-column prop="qty" label="数量" width="72" align="center" />
            <el-table-column label="进货价" width="96" align="right">
              <template slot-scope="scope">
                <span v-if="scope.row.purchasePrice != null">{{ Number(scope.row.purchasePrice).toFixed(4) }}</span>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <el-table-column prop="createTime" label="时间" width="160" />
          </el-table>
          <div v-if="!movementsInList.length" class="empty-tip">暂无入库记录</div>
        </div>
        <div class="movements-block">
          <div class="movements-title">出库记录（{{ movementsOutList.length }}）</div>
          <el-table :data="movementsOutList" size="small" border stripe max-height="280">
            <el-table-column prop="batchNo" label="出库批次" min-width="150" show-overflow-tooltip />
            <el-table-column prop="orderNo" label="订单号" min-width="110" show-overflow-tooltip />
            <el-table-column label="渠道" width="88" align="center">
              <template slot-scope="scope">{{ channelLabel(scope.row.channel) }}</template>
            </el-table-column>
            <el-table-column prop="baseName" label="底座" min-width="110" show-overflow-tooltip />
            <el-table-column prop="handleName" label="手柄" width="100" show-overflow-tooltip />
            <el-table-column prop="qty" label="数量" width="72" align="center" />
            <el-table-column prop="createTime" label="时间" width="160" />
          </el-table>
          <div v-if="!movementsOutList.length" class="empty-tip">暂无出库记录</div>
        </div>
      </div>
      <span slot="footer">
        <el-button size="small" @click="movementsVisible = false">关闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchSealRules,
  fetchEnabledSealBases,
  fetchEnabledSealHandles,
  fetchEnabledSealSuppliers,
  fetchSealPurchasePrice,
  fetchSealSkus,
  fetchEnabledSealSkus,
  updateSealSku,
  fetchSealSkuMovements,
  fetchSealStockChannels,
  fetchSealStockInList,
  fetchSealStockInDetail,
  createSealStockIn,
  fetchSealStockOutList,
  fetchSealStockOutDetail,
  createSealStockOut,
  ensureSealStockBoardToken
} from '@/api/h5-admin-seal-stock'

export default {
  name: 'H5AdminSealStock',
  data() {
    return {
      activeTab: 'stock',
      channels: [],
      ruleOptions: [],
      baseOptions: [],
      handleOptions: [],
      supplierOptions: [],
      skuOptions: [],
      skuDialogVisible: false,
      skuSaving: false,
      skuForm: { id: null, skuCode: '', skuName: '', stockQty: 0, ruleId: null, baseId: null, handleId: null, status: 1, remark: '' },
      stockLoading: false,
      stockList: [],
      stockTotal: 0,
      stockQuery: { page: 0, size: 10, keyword: '', ruleId: null, baseId: null, handleId: null },
      inLoading: false,
      inList: [],
      inTotal: 0,
      inQuery: { page: 0, size: 10, batchNo: '' },
      outLoading: false,
      outList: [],
      outTotal: 0,
      outQuery: { page: 0, size: 10, batchNo: '', orderNo: '', channel: null },
      inDialogVisible: false,
      inSubmitting: false,
      inForm: { supplierId: null, remark: '', items: [{ ruleId: null, baseId: null, handleId: null, qty: 1, purchasePrice: null }] },
      outDialogVisible: false,
      outSubmitting: false,
      outForm: {
        orderNo: '',
        channel: 'TAOBAO',
        receiverName: '',
        remark: '',
        items: [{ skuId: null, qty: 1 }]
      },
      detailVisible: false,
      detailTitle: '明细',
      detailHeader: null,
      detailItems: [],
      movementsVisible: false,
      movementsLoading: false,
      movementsTitle: '物料入出库记录',
      movementsSku: null,
      movementsInList: [],
      movementsOutList: []
    }
  },
  created() {
    this.loadChannels()
    this.loadStockFilterOptions()
    this.loadStockList()
  },
  methods: {
    toFullUrl(url) {
      if (!url) return ''
      const s = String(url).trim()
      if (/^https?:\/\//i.test(s) || s.startsWith('//') || s.startsWith('data:')) return s
      if (s.indexOf('/upload/') === 0 || s.indexOf('upload/') === 0) {
        return `https://oss-pai-s2lxmtw6mbk6ofo9ye-cn-shanghai.oss-cn-shanghai.aliyuncs.com/upload/${s.replace(/^\/?upload\/?/, '')}`
      }
      return s
    },
    skuOptionLabel(s) {
      if (!s) return ''
      const base = s.baseName || ''
      const handle = s.handleName || ''
      const name = s.skuName || s.skuCode || ''
      const parts = [name]
      if (base || handle) parts.push([base, handle].filter(Boolean).join(' / '))
      parts.push('库存 ' + (s.stockQty || 0))
      return parts.join(' · ')
    },
    buildStockBoardLink(token) {
      const origin = window.location.origin
      const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
      const prefix = base === '/' ? '' : base.replace(/\/$/, '')
      return `${origin}${prefix}/#/seal/stock-board?token=${encodeURIComponent(token)}`
    },
    copyStockBoardLink() {
      ensureSealStockBoardToken()
        .then((res) => {
          const data = (res && res.data) || {}
          const token = data.accessToken
          if (!token) {
            this.$message.error('生成看板令牌失败')
            return
          }
          const link = this.buildStockBoardLink(token)
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(link).then(() => {
              this.$message.success('库存看板链接已复制（免登录可打开）')
            }).catch(() => {
              this.$alert(link, '请手动复制链接', { confirmButtonText: '关闭' })
            })
          } else {
            this.$alert(link, '请手动复制链接', { confirmButtonText: '关闭' })
          }
        })
        .catch(() => {
          this.$message.error('获取看板链接失败，请确认已执行 stock_board SQL 并重启服务')
        })
    },
    channelLabel(code) {
      const hit = (this.channels || []).find((c) => c.code === code)
      return hit ? hit.label : (code || '—')
    },
    loadChannels() {
      fetchSealStockChannels()
        .then((res) => {
          this.channels = Array.isArray(res.data) ? res.data : []
        })
        .catch(() => {
          this.channels = []
        })
    },
    loadStockFilterOptions() {
      return Promise.all([
        fetchSealRules({ page: 0, size: 500, status: 1 }).then((res) => {
          const data = (res && res.data) || {}
          this.ruleOptions = data.content || []
        }),
        fetchEnabledSealBases().then((res) => {
          this.baseOptions = Array.isArray(res.data) ? res.data : []
        }),
        fetchEnabledSealHandles().then((res) => {
          this.handleOptions = Array.isArray(res.data) ? res.data : []
        })
      ]).catch(() => {})
    },
    loadOptions() {
      return Promise.all([
        this.loadStockFilterOptions(),
        fetchEnabledSealSuppliers().then((res) => {
          this.supplierOptions = Array.isArray(res.data) ? res.data : []
        }),
        fetchEnabledSealSkus().then((res) => {
          this.skuOptions = Array.isArray(res.data) ? res.data : []
        })
      ]).catch(() => {})
    },
    handleTabClick(tab) {
      const name = tab && tab.name
      if (name === 'stock') this.loadStockList()
      if (name === 'in') this.loadInList()
      if (name === 'out') this.loadOutList()
    },
    openSkuForm(row) {
      this.loadOptions().then(() => {
        this.skuForm = {
          id: row.id,
          skuCode: row.skuCode || '',
          skuName: row.skuName || '',
          stockQty: row.stockQty != null ? row.stockQty : 0,
          ruleId: row.ruleId || null,
          baseId: row.baseId || null,
          handleId: row.handleId || null,
          status: row.status != null ? row.status : 1,
          remark: row.remark || ''
        }
        this.skuDialogVisible = true
      })
    },
    openSkuMovements(row) {
      if (!row || !row.id) return
      this.movementsVisible = true
      this.movementsLoading = true
      this.movementsTitle = '物料入出库记录 · ' + (row.skuCode || row.id)
      this.movementsSku = {
        ...row,
        baseName: row.baseName,
        handleName: row.handleName
      }
      fetchSealSkuMovements(row.id)
        .then((res) => {
          const data = (res && res.data) || {}
          if (data.sku) {
            this.movementsSku = { ...this.movementsSku, ...data.sku }
          }
          this.movementsInList = data.inList || []
          this.movementsOutList = data.outList || []
        })
        .catch(() => {
          this.movementsInList = []
          this.movementsOutList = []
          this.$message.error('加载入出库记录失败')
        })
        .finally(() => {
          this.movementsLoading = false
        })
    },
    resetMovements() {
      this.movementsSku = null
      this.movementsInList = []
      this.movementsOutList = []
      this.movementsTitle = '物料入出库记录'
    },
    submitSkuForm() {
      if (!this.skuForm.ruleId || !this.skuForm.baseId || !this.skuForm.handleId) {
        this.$message.warning('请选择触点规则、印章底部与手柄')
        return
      }
      this.skuSaving = true
      updateSealSku({
        id: this.skuForm.id,
        ruleId: this.skuForm.ruleId,
        baseId: this.skuForm.baseId,
        handleId: this.skuForm.handleId,
        skuName: this.skuForm.skuName,
        status: this.skuForm.status,
        remark: this.skuForm.remark
      })
        .then(() => {
          this.$message.success('物料已更新')
          this.skuDialogVisible = false
          this.loadStockList()
        })
        .finally(() => {
          this.skuSaving = false
        })
    },
    onStockFilterChange() {
      this.stockQuery.page = 0
      this.loadStockList()
    },
    searchStockList() {
      this.stockQuery.page = 0
      this.loadStockList()
    },
    loadStockList() {
      this.stockLoading = true
      fetchSealSkus({
        page: this.stockQuery.page,
        size: this.stockQuery.size,
        keyword: this.stockQuery.keyword || undefined,
        ruleId: this.stockQuery.ruleId || undefined,
        baseId: this.stockQuery.baseId || undefined,
        handleId: this.stockQuery.handleId || undefined,
        status: 1
      })
        .then((res) => {
          const data = (res && res.data) || {}
          this.stockList = data.content || []
          this.stockTotal = data.totalElements || 0
        })
        .catch(() => {
          this.stockList = []
          this.stockTotal = 0
        })
        .finally(() => {
          this.stockLoading = false
        })
    },
    loadInList() {
      this.inLoading = true
      fetchSealStockInList({
        page: this.inQuery.page,
        size: this.inQuery.size,
        batchNo: this.inQuery.batchNo || undefined
      })
        .then((res) => {
          const data = (res && res.data) || {}
          this.inList = data.content || []
          this.inTotal = data.totalElements || 0
        })
        .catch(() => {
          this.inList = []
          this.inTotal = 0
        })
        .finally(() => {
          this.inLoading = false
        })
    },
    loadOutList() {
      this.outLoading = true
      fetchSealStockOutList({
        page: this.outQuery.page,
        size: this.outQuery.size,
        batchNo: this.outQuery.batchNo || undefined,
        orderNo: this.outQuery.orderNo || undefined,
        channel: this.outQuery.channel || undefined
      })
        .then((res) => {
          const data = (res && res.data) || {}
          this.outList = data.content || []
          this.outTotal = data.totalElements || 0
        })
        .catch(() => {
          this.outList = []
          this.outTotal = 0
        })
        .finally(() => {
          this.outLoading = false
        })
    },
    openInDialog() {
      this.loadOptions().then(() => {
        const defaultSupplierId = (this.supplierOptions[0] && this.supplierOptions[0].id) || null
        this.inForm = {
          supplierId: defaultSupplierId,
          remark: '',
          items: [{ ruleId: null, baseId: null, handleId: null, qty: 1, purchasePrice: null }]
        }
        this.inDialogVisible = true
      })
    },
    addInLine() {
      this.inForm.items.push({ ruleId: null, baseId: null, handleId: null, qty: 1, purchasePrice: null })
    },
    removeInLine(idx) {
      if (this.inForm.items.length <= 1) {
        this.$message.warning('至少保留一行')
        return
      }
      this.inForm.items.splice(idx, 1)
    },
    onInSupplierChange() {
      ;(this.inForm.items || []).forEach((item) => this.fillInPurchasePrice(item))
    },
    fillInPurchasePrice(item) {
      if (!item || !this.inForm.supplierId || !item.ruleId || !item.baseId || !item.handleId) {
        return
      }
      fetchSealPurchasePrice({
        supplierId: this.inForm.supplierId,
        ruleId: item.ruleId,
        baseId: item.baseId,
        handleId: item.handleId
      })
        .then((res) => {
          const data = (res && res.data) || {}
          if (data.purchasePrice != null) {
            this.$set(item, 'purchasePrice', Number(data.purchasePrice))
          }
        })
        .catch(() => {})
    },
    submitIn() {
      if (!this.inForm.supplierId) {
        this.$message.warning('请选择供应商')
        return
      }
      const items = (this.inForm.items || []).filter((i) => i.ruleId && i.baseId && i.handleId && i.qty > 0)
      if (!items.length) {
        this.$message.warning('请选择触点规则、印章底部与手柄')
        return
      }
      this.inSubmitting = true
      createSealStockIn({
        supplierId: this.inForm.supplierId,
        remark: this.inForm.remark,
        items: items.map((i) => ({
          ruleId: i.ruleId,
          baseId: i.baseId,
          handleId: i.handleId,
          qty: i.qty,
          purchasePrice: i.purchasePrice
        }))
      })
        .then((res) => {
          const header = (res && res.data) || {}
          this.$message.success('入库成功，批次：' + (header.batchNo || ''))
          this.inDialogVisible = false
          this.loadStockList()
          if (this.activeTab === 'in') this.loadInList()
        })
        .finally(() => {
          this.inSubmitting = false
        })
    },
    openOutDialog() {
      this.loadOptions().then(() => {
        this.outForm = {
          orderNo: '',
          channel: 'TAOBAO',
          receiverName: '',
          remark: '',
          items: [{ skuId: null, qty: 1 }]
        }
        this.outDialogVisible = true
      })
    },
    addOutLine() {
      this.outForm.items.push({ skuId: null, qty: 1 })
    },
    removeOutLine(idx) {
      if (this.outForm.items.length <= 1) {
        this.$message.warning('至少保留一行')
        return
      }
      this.outForm.items.splice(idx, 1)
    },
    submitOut() {
      if (!this.outForm.channel) {
        this.$message.warning('请选择出库渠道')
        return
      }
      const items = (this.outForm.items || []).filter((i) => i.skuId && i.qty > 0)
      if (!items.length) {
        this.$message.warning('请选择出库物料')
        return
      }
      this.outSubmitting = true
      createSealStockOut({
        orderNo: this.outForm.orderNo,
        channel: this.outForm.channel,
        receiverName: this.outForm.receiverName,
        remark: this.outForm.remark,
        items
      })
        .then((res) => {
          const header = (res && res.data) || {}
          this.$message.success('出库成功，批次：' + (header.batchNo || ''))
          this.outDialogVisible = false
          this.loadStockList()
          if (this.activeTab === 'out') this.loadOutList()
        })
        .finally(() => {
          this.outSubmitting = false
        })
    },
    openInDetail(row) {
      fetchSealStockInDetail(row.id).then((res) => {
        const data = (res && res.data) || {}
        this.detailTitle = '入库明细'
        this.detailHeader = data.header
        this.detailItems = data.items || []
        this.detailVisible = true
      })
    },
    openOutDetail(row) {
      fetchSealStockOutDetail(row.id).then((res) => {
        const data = (res && res.data) || {}
        this.detailTitle = '出库明细'
        this.detailHeader = data.header
        this.detailItems = data.items || []
        this.detailVisible = true
      })
    }
  }
}
</script>

<style scoped>
.seal-stock-page .stock-filter-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.seal-stock-page .stock-filter-item {
  width: 168px;
}
.seal-stock-page .stock-filter-keyword {
  width: 180px;
}
/* 入库记录展开子表：物料编码与父行「入库批次」列对齐 */
.seal-stock-page .in-record-table ::v-deep .el-table__expanded-cell {
  padding: 8px 0 8px 48px;
}
.seal-stock-page .in-record-expand-table {
  border-left: 2px solid #dcdfe6;
}
.seal-stock-page .in-record-expand-table ::v-deep th:first-child .cell,
.seal-stock-page .in-record-expand-table ::v-deep td:first-child .cell {
  position: relative;
  padding-left: 14px;
}
.seal-stock-page .in-record-expand-table ::v-deep td:first-child .cell::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 8px;
  height: 1px;
  background: #c0c4cc;
}
.seal-stock-page .thumb-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  background: #fafafa;
}
.seal-stock-page .sku-link {
  padding: 0;
  font-weight: 600;
}
.seal-stock-page .sub-line {
  margin-top: 2px;
  color: #909399;
  font-size: 12px;
  line-height: 1.3;
}
.seal-stock-page .muted {
  color: #c0c4cc;
}
.seal-stock-page .qty-num {
  font-weight: 700;
  color: #303133;
}
.seal-stock-page .qty-num.is-empty {
  color: #f56c6c;
}
.seal-stock-page .form-tip {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
}
.seal-stock-page .movements-block {
  margin-bottom: 16px;
}
.seal-stock-page .movements-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #303133;
}
.seal-stock-page .empty-tip {
  margin-top: 8px;
  color: #909399;
  font-size: 12px;
}
</style>
